import type { CSSProperties } from 'react';

export type TAuthPathIframeHeight = CSSProperties['height'];

export interface IAuthPathIframeConfig {
  iframeHeight?: TAuthPathIframeHeight;
}

export interface IAuthPathPageConfig {
  config?: IAuthPathIframeConfig & {
    authPathConfigs?: Record<string, IAuthPathIframeConfig>;
  };
}

export const normalizeIframeHeight = (
  height?: TAuthPathIframeHeight,
): string | undefined => {
  if (typeof height === 'number' && Number.isFinite(height)) {
    return `${height}px`;
  }
  if (typeof height === 'string' && height.trim()) {
    return height.trim();
  }
  return undefined;
};

export const resolveAuthPathIframeHeight = (
  pageConfigHeight?: TAuthPathIframeHeight,
  propHeight?: TAuthPathIframeHeight,
  adminHeight?: TAuthPathIframeHeight,
): string | undefined => normalizeIframeHeight(pageConfigHeight)
  ?? normalizeIframeHeight(propHeight)
  ?? normalizeIframeHeight(adminHeight);

export const getAuthPathIframeHeight = (
  pageConfig?: IAuthPathPageConfig,
  tabNameEn?: string,
): string | undefined => {
  if (tabNameEn !== undefined) {
    const configKey = String(tabNameEn).trim();
    if (!configKey) return undefined;
    return normalizeIframeHeight(
      pageConfig?.config?.authPathConfigs?.[configKey]?.iframeHeight,
    );
  }

  return normalizeIframeHeight(pageConfig?.config?.iframeHeight);
};

export const safeDecodePageConfigPath = (path: string): string => {
  const pathname = String(path || '').split(/[?#]/, 1)[0];
  const normalizedPath = pathname
    ? `/${pathname.replace(/^\/+/, '')}`
    : '';
  try {
    return decodeURIComponent(normalizedPath);
  } catch (_) {
    return normalizedPath;
  }
};

export const getStudioGamePageConfigPath = (pathname: string): string => {
  const pathSegments = String(pathname || '')
    .split(/[?#]/, 1)[0]
    .split('/')
    .filter(Boolean);
  const studioIndex = pathSegments.indexOf('studio');
  if (studioIndex < 0 || pathSegments.length <= studioIndex + 3) {
    return '';
  }
  return safeDecodePageConfigPath(
    pathSegments.slice(studioIndex + 3).join('/'),
  );
};
